import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import ProblemSolution from "@/components/ProblemSolution";
import JobLifecycle from "@/components/JobLifecycle";
import ProcessModules from "@/components/ProcessModules";
import EInvoiceHomeBanner from "@/components/EInvoiceHomeBanner";
import ClientLogos from "@/components/ClientLogos";
import SmallBusinessSection from "@/components/SmallBusinessSection";
import FinalCTA from "@/components/FinalCTA";
import Footer from "@/components/Footer";
import { SEO, createOrganizationSchema, createSoftwareApplicationSchema, createWebPageSchema, createFAQSchema, createHowToSchema } from "@/components/SEO";
import { PageLoading } from "@/components/ui/loading";
import IndustryProofStrip from "@/components/IndustryProofStrip";
import ViewportLazy from "@/components/ViewportLazy";
import { InteractiveProductShowcase } from "@/components/showcase/InteractiveProductShowcase";
import { WhoAreYouSection } from "@/components/worlds/WhoAreYouSection";
import { getWorldsContent } from "@/components/worlds/worldsRegistry";

const HomeVideoShowcase = lazy(() =>
  import("@/components/video/HomeVideoShowcase").then((module) => ({ default: module.HomeVideoShowcase }))
);
const ProfessionsCarousel = lazy(() => import("@/components/ProfessionsCarousel"));
const SuccessStories = lazy(() => import("@/components/SuccessStories"));
const SecurityComplianceEnhanced = lazy(() => import("@/components/SecurityComplianceEnhanced"));
const ROICalculator = lazy(() => import("@/components/ROICalculator"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const FAQ = lazy(() => import("@/components/FAQ"));

const Index = () => {
  const { t, i18n } = useTranslation();
  const worlds = getWorldsContent(i18n.language);

  const homeFaqs = t('pages.home.faq.items', { returnObjects: true }) as Array<{ question: string; answer: string }>;
  const lifecycleSteps = t('pages.home.jobLifecycle.steps', { returnObjects: true }) as Array<{
    title: string;
    summary: string;
  }>;

  const structuredData = [
    createOrganizationSchema(i18n.language),
    createSoftwareApplicationSchema(i18n.language),
    createWebPageSchema({
      name: t('landing.hero.title'),
      description: t('seo.home.description'),
      url: typeof window !== 'undefined' ? window.location.href : 'https://bidmio.com'
    }),
    ...(Array.isArray(homeFaqs) && homeFaqs.length > 0
      ? [createFAQSchema(homeFaqs.map((faq) => ({ question: faq.question, answer: faq.answer })))]
      : []),
    ...(Array.isArray(lifecycleSteps) && lifecycleSteps.length > 0
      ? [createHowToSchema({
          name: t('pages.home.jobLifecycle.title'),
          description: t('pages.home.jobLifecycle.subtitle'),
          steps: lifecycleSteps.map((step) => ({ name: step.title, text: step.summary })),
        })]
      : []),
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title={t('seo.home.title')}
        description={t('seo.home.description')}
        keywords={t('seo.home.keywords')}
        structuredData={structuredData}
      />
      <Header />
      <main className="home-content">
        <HeroSection />
        {/* Fully synchronous SSR content: boundaries let React yield between
            hydration tasks and prioritize whichever section receives input. */}
        <Suspense fallback={null}>
          {worlds ? <WhoAreYouSection content={worlds} /> : null}
        </Suspense>
        <Suspense fallback={null}>
          <EInvoiceHomeBanner />
          <ProblemSolution />
        </Suspense>
        <Suspense fallback={null}>
          <InteractiveProductShowcase page="home" />
        </Suspense>
        <Suspense fallback={null}>
          <JobLifecycle />
          <IndustryProofStrip context="home" />
        </Suspense>
        <ViewportLazy fallback={null}>
          <HomeVideoShowcase />
        </ViewportLazy>
        <Suspense fallback={null}>
          <ProcessModules />
        </Suspense>
        <ViewportLazy fallback={<PageLoading />}>
          <ProfessionsCarousel />
        </ViewportLazy>
        <Suspense fallback={null}>
          <ClientLogos />
          <SmallBusinessSection />
        </Suspense>
        <ViewportLazy fallback={<PageLoading />}>
          <SuccessStories />
        </ViewportLazy>
        <ViewportLazy fallback={<PageLoading />}>
          <SecurityComplianceEnhanced />
        </ViewportLazy>
        <ViewportLazy fallback={<PageLoading />}>
          <ROICalculator />
        </ViewportLazy>
        <ViewportLazy fallback={<PageLoading />}>
          <Testimonials />
        </ViewportLazy>
        <ViewportLazy fallback={<PageLoading />}>
          <FAQ />
        </ViewportLazy>
        <Suspense fallback={null}>
          <FinalCTA />
        </Suspense>
      </main>
      <Footer showCta={false} />
    </div>
  );
};

export default Index;
